import { io, Socket as SocketIOSocket } from 'socket.io-client';

export interface ISocket {
    id?: string;
    connected: boolean;
    on(event: string, callback: (...args: any[]) => void): void;
    off(event: string, callback?: (...args: any[]) => void): void;
    emit(event: string, data?: any): void;
    disconnect(): void;
    connect(): void;
}

const AUTH_TOKEN_KEY = 'auth_token';

function readStoredToken(): string | null {
    try {
        return localStorage.getItem(AUTH_TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

function withToken(url: string, token: string | null): string {
    if (!token) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}token=${encodeURIComponent(token)}`;
}

function normalizeSocketUrl(url: string): string {
    if (!url) return url;
    const trimmedUrl = url.trim();
    if (/^wss:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(trimmedUrl)) {
        return trimmedUrl.replace(/^wss:\/\//i, 'ws://');
    }
    return trimmedUrl;
}

export class SocketIOAdapter implements ISocket {
    private socket: SocketIOSocket;

    constructor(url: string, options?: any) {
        this.socket = io(url, options);
    }

    get id() { return this.socket.id; }
    get connected() { return this.socket.connected; }

    on(event: string, callback: (...args: any[]) => void): void {
        this.socket.on(event, callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        this.socket.off(event, callback);
    }

    emit(event: string, data?: any): void {
        this.socket.emit(event, data);
    }

    disconnect(): void {
        this.socket.disconnect();
    }

    connect(): void {
        this.socket.connect();
    }
}

const PING_INTERVAL_MS = 4 * 60 * 1000;
const PONG_TIMEOUT_MS = 10 * 1000;
const UNSUBSCRIBE_EVENTS = new Set([
    'leave_room',
    'unsubscribe',
    'unsubscribe-business-account',
    'unsubscribe-campaign',
]);

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30 * 1000;
const RECONNECT_JITTER = 0.2;

export class ServerlessWebSocketAdapter implements ISocket {
    private ws: WebSocket | null = null;
    private url: string;
    private eventListeners: Map<string, Set<Function>> = new Map();

    private activeSubscriptions: Set<string> = new Set();
    private lastAuthPayload: any = null;

    private reconnectAttempts = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private pingTimer: ReturnType<typeof setInterval> | null = null;
    private pongTimer: ReturnType<typeof setTimeout> | null = null;
    private intentionalClose = false;
    private hasConnectedOnce = false;

    public id?: string;
    public connected: boolean = false;

    private getToken: () => string | null;

    constructor(url: string, getToken: () => string | null = readStoredToken) {
        this.url = url;
        this.getToken = getToken;
        this.connect();
    }

    connect(): void {
        this.intentionalClose = false;
        this.clearReconnectTimer();

        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;

        const token = this.getToken();
        if (!token) {
            console.warn(
                '[Socket] connecting WITHOUT a token — the connection will be anonymous and realtime will be empty',
            );
        }

        try {
            this.ws = new WebSocket(withToken(this.url, token));
        } catch (err) {
            this._trigger('connect_error', err);
            this.scheduleReconnect();
            return;
        }

        this.ws.onopen = () => {
            this.connected = true;
            this.reconnectAttempts = 0;

            this.replaySessionState();
            this.startPing();

            if (this.hasConnectedOnce) {
                this._trigger('reconnect', this.reconnectAttempts);
            } else {
                this.hasConnectedOnce = true;
                this._trigger('connect', {});
            }
        };

        this.ws.onclose = () => {
            this.connected = false;
            this.stopPing();
            this._trigger('disconnect', 'closed');
            if (!this.intentionalClose) this.scheduleReconnect();
        };

        this.ws.onerror = (err) => {
            this._trigger('connect_error', err);
        };

        this.ws.onmessage = (msg) => {
            try {
                const parsed = JSON.parse(msg.data);

                if (parsed.action === 'pong') {
                    this.clearPongTimer();
                    return;
                }

                if (parsed.action && parsed.data) {
                    this._trigger(parsed.action, parsed.data);
                } else if (parsed.action) {
                    this._trigger(parsed.action, parsed);
                }
            } catch (e) {
            }
        };
    }

    private scheduleReconnect(): void {
        if (this.intentionalClose || this.reconnectTimer) return;

        const exponential = Math.min(
            RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
            RECONNECT_MAX_MS,
        );
        const jitter = exponential * RECONNECT_JITTER * (Math.random() * 2 - 1);
        const delay = Math.max(RECONNECT_BASE_MS, Math.round(exponential + jitter));

        this.reconnectAttempts += 1;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    private replaySessionState(): void {
        if (this.lastAuthPayload) {
            this.sendRaw({ action: 'authenticate', ...this.lastAuthPayload });
        }
        for (const roomId of this.activeSubscriptions) {
            this.sendRaw({ action: 'subscribe', roomId });
        }
    }

    private startPing(): void {
        this.stopPing();
        this.pingTimer = setInterval(() => {
            if (!this.sendRaw({ action: 'ping' })) return;
            this.clearPongTimer();
            this.pongTimer = setTimeout(() => {
                this.pongTimer = null;
                try { this.ws?.close(); } catch { /* already gone */ }
            }, PONG_TIMEOUT_MS);
        }, PING_INTERVAL_MS);
    }

    private stopPing(): void {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
        this.clearPongTimer();
    }

    private clearPongTimer(): void {
        if (this.pongTimer) {
            clearTimeout(this.pongTimer);
            this.pongTimer = null;
        }
    }

    private _trigger(event: string, ...args: any[]) {
        const listeners = this.eventListeners.get(event);
        if (listeners) {
            Array.from(listeners).forEach(cb => {
                try { cb(...args); } catch (err) { console.error(`[Socket] listener for "${event}" threw:`, err); }
            });
        }
    }

    on(event: string, callback: (...args: any[]) => void): void {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, new Set());
        }
        this.eventListeners.get(event)!.add(callback);
    }

    off(event: string, callback?: (...args: any[]) => void): void {
        if (!callback) {
            this.eventListeners.delete(event);
        } else {
            this.eventListeners.get(event)?.delete(callback);
        }
    }

    private sendRaw(payload: Record<string, any>): boolean {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
        try {
            this.ws.send(JSON.stringify(payload));
            return true;
        } catch (err) {
            this._trigger('connect_error', err);
            try { this.ws.close(); } catch { /* already gone */ }
            return false;
        }
    }

    emit(event: string, data?: any): void {
        if (event === 'authenticate') {
            this.lastAuthPayload = data ?? {};
        }

        const roomId = this.roomIdFor(event, data);

        if (UNSUBSCRIBE_EVENTS.has(event)) {
            if (roomId) {
                this.activeSubscriptions.delete(roomId);
                this.sendRaw({ action: 'unsubscribe', roomId });
            }
            return;
        }

        if (roomId) {
            this.activeSubscriptions.add(roomId);
            this.sendRaw({ action: 'subscribe', roomId });
            return;
        }

        this.sendRaw({ action: event, ...data });
    }

    private roomIdFor(event: string, data?: any): string | null {
        switch (event) {
            case 'join_room':
            case 'subscribe':
            case 'leave_room':
            case 'unsubscribe':
                return (typeof data === 'string' ? data : data?.roomId) ?? null;
            case 'subscribe-business-account':
            case 'unsubscribe-business-account': {
                const id = typeof data === 'string' ? data : data?.businessAccountId ?? data;
                return id ? `business-account-${id}` : null;
            }
            case 'subscribe-campaign':
            case 'unsubscribe-campaign': {
                const id = typeof data === 'string' ? data : data?.campaignId ?? data;
                return id ? `campaign-${id}` : null;
            }
            default:
                return null;
        }
    }

    disconnect(): void {
        this.intentionalClose = true;
        this.clearReconnectTimer();
        this.stopPing();
        this.activeSubscriptions.clear();
        this.lastAuthPayload = null;
        try { this.ws?.close(); } catch { /* already gone */ }
    }
}

export function createSocket(url: string, options?: any): ISocket {
    const resolvedUrl = normalizeSocketUrl(url);

    if (resolvedUrl.startsWith('ws://') || resolvedUrl.startsWith('wss://')) {
        if (resolvedUrl.includes('execute-api')) {
            return new ServerlessWebSocketAdapter(resolvedUrl, options?.getToken);
        }
    }
    return new SocketIOAdapter(resolvedUrl, options);
}
